/* eslint-disable unicorn/filename-case -- kebab-case per docs/conventions/core.md: this file
   exports a hook, not a React component. Matches the same exemption on
   table-internals/use-row-keyboard-navigation.ts. */
import {
  type ColumnFiltersState,
  type ExpandedState,
  type OnChangeFn,
  type RowData,
  type SortingState,
  useTable,
} from '@tanstack/react-table';
import * as React from 'react';

import type { DimeTreeTableProps, VisibilityState } from './DimeTreeTable.types.js';
import { dimeTreeTableFeatures, type DimeTreeTableFeatures } from './tree-table-features.js';

type TreeTableOptions<TData extends RowData> = Pick<
  DimeTreeTableProps<TData>,
  | 'columnFilters'
  | 'columns'
  | 'columnVisibility'
  | 'data'
  | 'defaultAllExpanded'
  | 'expanded'
  | 'getRowCanExpand'
  | 'getRowId'
  | 'getSubRows'
  | 'isLoading'
  | 'manualFiltering'
  | 'manualSorting'
  | 'onColumnFiltersChange'
  | 'onColumnVisibilityChange'
  | 'onExpandedChange'
  | 'onUpdateSortBy'
  | 'sortBy'
>;

/** Every node with children, expanded — the one-time seed for `defaultAllExpanded`. Walked over
 *  the raw `data` tree via `getSubRows` directly, since this runs before the table exists.
 *
 *  Mirrors collect-expandable-row-ids.ts's getCanExpand guard: a vetoed node's children must not
 *  be marked expanded either, since TanStack's createExpandedRowModel only checks this seed's
 *  expanded-state map (not the veto) when deciding whether to flatten a node's subRows into the
 *  visible list — an unguarded seed would render a vetoed node's children even though its own
 *  chevron/aria-expanded correctly stay hidden, with no visible way the row could have expanded. */
function computeAllExpandedIds<TData>({
  data,
  getSubRows,
  getRowId,
  getRowCanExpand,
}: {
  data: readonly TData[];
  getSubRows: (node: TData) => TData[] | undefined;
  getRowId: (node: TData) => string;
  getRowCanExpand?: (node: TData) => boolean;
}): Record<string, boolean> {
  const expandedIds: Record<string, boolean> = {};
  const walk = (nodes: readonly TData[]) => {
    for (const node of nodes) {
      const children = getSubRows(node);
      if (children && children.length > 0 && (getRowCanExpand?.(node) ?? true)) {
        expandedIds[getRowId(node)] = true;
        walk(children);
      }
    }
  };
  walk(data);
  return expandedIds;
}

/** Builds the tree TanStack table: controlled-with-uncontrolled-fallback state for expansion,
 *  sorting, filtering and visibility, wired to `getSubRows` for arbitrary-depth hierarchy. */
export function useTreeTable<TData extends RowData>({
  columns,
  data,
  getRowId,
  getSubRows,
  expanded,
  onExpandedChange,
  defaultAllExpanded = false,
  sortBy,
  onUpdateSortBy,
  manualSorting = false,
  columnFilters,
  onColumnFiltersChange,
  manualFiltering = false,
  columnVisibility,
  onColumnVisibilityChange,
  getRowCanExpand,
  isLoading = false,
}: TreeTableOptions<TData>) {
  const [internalExpanded, setInternalExpanded] = React.useState<Record<string, boolean>>(() =>
    defaultAllExpanded
      ? computeAllExpandedIds({ data, getSubRows, getRowId, getRowCanExpand })
      : {},
  );
  const resolvedExpanded = expanded ?? internalExpanded;
  const resolvedOnExpandedChange = onExpandedChange ?? setInternalExpanded;

  const [internalSorting, setInternalSorting] = React.useState<SortingState>([]);
  const resolvedSorting = sortBy ?? internalSorting;
  const resolvedOnSortingChange = onUpdateSortBy ?? setInternalSorting;

  const [internalColumnFilters, setInternalColumnFilters] = React.useState<ColumnFiltersState>([]);
  const resolvedColumnFilters = columnFilters ?? internalColumnFilters;
  const resolvedOnColumnFiltersChange = onColumnFiltersChange ?? setInternalColumnFilters;

  const [internalVisibility, setInternalVisibility] = React.useState<VisibilityState>({});
  const resolvedVisibility = columnVisibility ?? internalVisibility;
  const resolvedOnVisibilityChange = onColumnVisibilityChange ?? setInternalVisibility;

  // filterFromLeafRows (below) keeps a match's ancestors IN the filtered row model, but that alone
  // does not make them VISIBLE -- expansion is a separate, later pipeline stage, and an ancestor's
  // own `expanded` flag is whatever it already was (usually false) before the user typed anything.
  // Without this, searching "Gummies" while collapsed would filter the row model correctly and
  // still render an empty table, because nothing forces the surviving ancestor chain open.
  //
  // This overrides only what gets RENDERED, never the underlying expanded value itself (controlled
  // or internal) -- clearing the filter must reveal exactly the expansion state from before the
  // search. That invariant depends on nothing writing back into `expanded` while it holds: index.tsx
  // uses `hasActiveFilter` (returned below) to make the chevron and keyboard expand/collapse
  // handlers inert while a filter is active, since TanStack's toggleExpanded would otherwise read
  // the `true` sentinel below, materialize it into a concrete expanded-map over every visible row,
  // and write that back through onExpandedChange -- silently replacing the real state with
  // "everything expanded minus the one row clicked" once the filter clears.
  const hasActiveColumnFilter = resolvedColumnFilters.length > 0;

  const table = useTable({
    features: dimeTreeTableFeatures,
    data,
    columns: columns as unknown as Parameters<
      typeof useTable<DimeTreeTableFeatures, TData>
    >[0]['columns'],
    getRowId: (row) => getRowId(row as TData),
    getSubRows: (row) => getSubRows(row as TData),
    state: {
      expanded: hasActiveColumnFilter ? true : resolvedExpanded,
      sorting: resolvedSorting,
      columnFilters: resolvedColumnFilters,
      columnVisibility: resolvedVisibility,
    },
    // ExpandedState widens to `true`, which no prop here can produce (nothing calls
    // toggleAllRowsExpanded — defaultAllExpanded's `true` sentinel never reaches this cast, only
    // the internal pre-computed Record above), so Record<string, boolean> is the only observable
    // shape a consumer's onExpandedChange ever receives.
    onExpandedChange: resolvedOnExpandedChange as OnChangeFn<ExpandedState>,
    onSortingChange: resolvedOnSortingChange,
    onColumnFiltersChange: resolvedOnColumnFiltersChange,
    onColumnVisibilityChange: resolvedOnVisibilityChange,
    manualSorting,
    manualFiltering,
    // A match keeps its ancestor chain visible instead of the path to it disappearing behind a
    // filtered-out, collapsed parent — see DimeTreeTable.md's "Search semantics".
    filterFromLeafRows: true,
    // Without this, every render whose `data` reference changed (a consumer that doesn't memoize
    // its tree, a brand switch, a refetch) schedules a reset that calls onExpandedChange({}) with
    // no user action behind it -- wiping a controlled consumer's expansion state. This is the
    // OPPOSITE fix from DimeNestedTable's `manualExpanding: true`, which suppresses the identical
    // reset by disabling expansion computation entirely. That works there because DimeNestedTable
    // never uses getSubRows and has no row-model computation to disable around; DimeTreeTable's
    // entire premise is that the built-in expandedRowModel turns getSubRows + `expanded` state
    // into the actual flattened, depth-ordered visible row list this component renders, so
    // manualExpanding must stay unset here and autoResetExpanded is the correct lever instead.
    autoResetExpanded: false,
    getRowCanExpand: (row) =>
      row.subRows.length > 0 && (getRowCanExpand ? getRowCanExpand(row.original as TData) : true),
    meta: { isLoading },
  });

  return { table, hasActiveFilter: hasActiveColumnFilter };
}
